import { Tooltip, IconButton, ButtonProps, useToast } from '@chakra-ui/react';
import { ApiRequestType, useApiRequest } from '@texas/api/hooks/useApiRequest';
import { request } from '@texas/utils/helpers/httpHelpers';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

interface Props<A extends any[], R> extends ButtonProps {
  tooltip: string;
  icon: ReactElement;

  onPerformed: (data: R) => void;
  primaryRequest: ApiRequestType<A, R>;
  args: A;
}

export function AsyncIconButton<A extends any[], R>({
  tooltip,
  icon,
  onPerformed,
  primaryRequest,
  args,
  ...props
}: Props<A, R>) {
  const { t } = useTranslation();
  const toast = useToast();

  const { request: remoteRequest, loading } = useApiRequest(primaryRequest);

  async function performRequest() {
    await request(
      remoteRequest,
      args,
      (data) => {
        onPerformed(data);
      },
      (error) => {
        toast({
          title: t('general.actionFailed'),
          description: error.message,
          status: 'error',
          isClosable: true,
        });
      },
    );
  }

  return (
    <Tooltip label={tooltip}>
      <IconButton
        {...props}
        isDisabled={loading}
        isLoading={loading}
        icon={icon}
        aria-label={tooltip}
        onClick={async () => {
          await performRequest();
        }}
      />
    </Tooltip>
  );
}
